// @flow

// The event that brought up the crash screen must reach Sentry whatever the client
// filters say. It is marked with this tag in the ErrorBoundary scope (beforeCapture).
// A separate tag on purpose: force_keep alone only skips sampling in beforeSend.
export const CRASH_EVENT_TAG = 'crash_screen'

export const isCrashEvent = (event: ?Object): boolean =>
  Boolean(event && event.tags && event.tags[CRASH_EVENT_TAG] === '1')

// Same value as PLAYBACK_FAILURE_TAG of the player. Kept here so that this module, which the crash
// screen relies on, imports nothing; a test keeps the two equal
export const PLAYBACK_FAILURE_TAG = 'playback_failure'

// The final event of a failed playback cycle. The SDK Dedupe would drop a failure of other content
// that looks the same; the player dedupes these events itself. InboundFilters still applies to it
export const isPlaybackFailureEvent = (event: ?Object): boolean =>
  Boolean(event && event.tags && event.tags[PLAYBACK_FAILURE_TAG] === '1')

const isDedupeSkipped = (event: ?Object): boolean =>
  isCrashEvent(event) || isPlaybackFailureEvent(event)

const BYPASSES = {
  InboundFilters: isCrashEvent,
  Dedupe: isDedupeSkipped,
}

// Delegates to the SDK integration unchanged, skipping it for the events isSkipped selects
// (the crash event by default). Relies on processEvent of @sentry/react 7.93; the tests guard
// this on SDK updates.
export const skipForCrashEvent = (
  integration: Object,
  isSkipped: (event: ?Object) => boolean = isCrashEvent,
): Object => ({
  name: integration.name,
  setupOnce: (...args: Array<mixed>) => integration.setupOnce(...args),
  processEvent: (event: Object, hint: Object, client: Object) =>
    isSkipped(event) ? event : integration.processEvent(event, hint, client),
})

// For the `integrations` option of Sentry.init
export const withCrashEventBypass = (integrations: Array<Object>): Array<Object> =>
  integrations.map((integration) =>
    BYPASSES[integration.name]
      ? skipForCrashEvent(integration, BYPASSES[integration.name])
      : integration,
  )
